"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu } from "lucide-react";

import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DOCS_TABS } from "./nav";
import { PRIMARY_GRADIENT } from "./theme";
import { DocsSidebar } from "./sidebar";

function isTabActive(pathname: string | null, href?: string) {
  if (!pathname || !href) return false;
  if (href === "/docs") return pathname.startsWith("/docs") && pathname !== "/docs/mcp";
  return pathname === href;
}

export function DocsHeader() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-14 max-w-screen-2xl items-center gap-4 px-4 lg:px-8">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              aria-label="Open navigation"
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted/60 hover:text-foreground lg:hidden"
            >
              <Menu className="size-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[300px] overflow-y-auto pt-10">
            <DocsSidebar onNavigate={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>

        <Link href="/" className="flex shrink-0 items-center gap-2">
          <Image
            src="/_static/landing/quantwheelicon-transparent.png"
            alt="QuantWheel"
            width={32}
            height={32}
            className="size-7"
          />
          <span className="text-base font-medium tracking-tight">QuantWheel</span>
          <span className="rounded-md border border-border/60 bg-muted/60 px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
            Docs
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {DOCS_TABS.map((tab) =>
            tab.soon || !tab.href ? (
              <span
                key={tab.label}
                className="flex cursor-default items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-muted-foreground/60"
              >
                {tab.label}
                <span className="rounded-full border border-border/60 px-1.5 py-px text-[10px] text-muted-foreground/70">
                  Soon
                </span>
              </span>
            ) : (
              <Link
                key={tab.label}
                href={tab.href}
                className={cn(
                  "rounded-lg px-3 py-1.5 text-sm transition-colors",
                  isTabActive(pathname, tab.href)
                    ? cn("font-medium", PRIMARY_GRADIENT)
                    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                )}
              >
                {tab.label}
              </Link>
            ),
          )}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <Link
            href="/dashboard"
            className={cn(
              "hidden shrink-0 whitespace-nowrap rounded-lg px-3.5 py-1.5 text-sm font-medium sm:inline-flex",
              PRIMARY_GRADIENT,
            )}
          >
            Open Dashboard
          </Link>
        </div>
      </div>
    </header>
  );
}
